import React, { createContext, ReactNode, useCallback, useMemo } from 'react';
import { DeliveryStrategyType } from '../../../../../../@types/deliveries.enum';
import { useDeliveryStrategyApi } from '../../../../../api/commerce/Delivery';
import { validateAgainstGiftcards } from './deliveryHelpers';

interface IDeliveryStrategyContext {
  availableStrategies: DeliveryStrategyType[];
  availableStrategyCount: number;
  deliverySelectionBuckets: Deliveries.IDeliverySelection[];
  shippingBuckets: Deliveries.IShippingBucket[];
  selectedStrategyType: DeliveryStrategyType;
  selectStrategy: (strategy: DeliveryStrategyType) => void;
  selectStrategyError: Commerce.CommerceException | null;
  resetSelectStrategyError: () => void;
  settingStrategy: boolean;
  strategySelectionTotals: Deliveries.IDeliveryStrategyTotals;
  strategyAddress?: Commerce.IDeliveryAddress;
  isAllStorePickup: boolean;
  isAnyMioHomeDelivery: boolean;
  deliveryDiscounts: Deliveries.IDeliveryDiscount[];
  assemblyDiscounts: Deliveries.IAssemblyDiscount[];
  strategySelection: Deliveries.IDeliveryStrategy;
  strategySelections: Deliveries.IStrategySelections | undefined;
  updateAssemblyItems: (assemblyRequest: Deliveries.IDeliveryAssemblyRequest) => void;
  settingAssemblyItems: boolean;
  availableAssemblyItems: Deliveries.IShippingAssemblyItem[];
  buildNewStrategySelections: (
    strategySelection: Deliveries.IDeliveryStrategy,
  ) => Deliveries.IStrategySelections;
}

export const DeliveryStrategyContext = createContext<IDeliveryStrategyContext | undefined>(
  undefined,
);

interface IDeliveryStrategyProvider {
  children: ReactNode;
}

const emptyStrategySelectionTotals = {
  delivery: 0,
  assembly: 0,
  total: 0,
  discounts: 0,
  originalTotal: 0,
  vat: 0,
} as Deliveries.IDeliveryStrategyTotals;

const DeliveryStrategyProvider = ({ children }: IDeliveryStrategyProvider) => {
  const {
    deliveryStrategies,
    setShippingStrategy,
    setShippingStrategyError,
    resetSetShippingStrategyError,
    settingStrategy,
    deliverySelections,
    setAssemblyItems,
    settingAssemblyItems,
    availableAssemblyItems,
  } = useDeliveryStrategyApi(false);

  const selectedStrategyType = deliverySelections?.selectedStrategyType;
  const selectedStrategyString = DeliveryStrategyType[selectedStrategyType];
  const strategySelections = deliverySelections?.strategySelections ?? {};

  const strategySelection =
    strategySelections?.[selectedStrategyString] ?? ({} as Deliveries.IDeliveryStrategy);
  const deliverySelectionBuckets =
    strategySelection?.deliverySelections ?? ([] as Deliveries.IDeliverySelection[]);

  const selectStrategy = useCallback(
    (deliveryStrategyType: DeliveryStrategyType) => {
      if (deliveryStrategyType != null) {
        setShippingStrategy({ deliveryStrategyType });
      }
    },
    [setShippingStrategy],
  );

  const buildNewStrategySelections = useCallback(
    (newStrategySelection: Deliveries.IDeliveryStrategy): Deliveries.IStrategySelections => ({
      ...strategySelections,
      [selectedStrategyString]: newStrategySelection,
    }),
    [strategySelections, selectedStrategyString],
  );

  const updateAssemblyItems = useCallback(
    (assemblyRequest: Deliveries.IDeliveryAssemblyRequest) => {
      setAssemblyItems(assemblyRequest);
    },
    [],
  );

  const selectStrategyError = setShippingStrategyError;
  const resetSelectStrategyError = resetSetShippingStrategyError;

  const validDeliveryStrategies = validateAgainstGiftcards(deliveryStrategies);

  const availableStrategies =
    validDeliveryStrategies?.map((s) => s.type as DeliveryStrategyType) ?? [];

  const availableStrategyCount = availableStrategies.length;

  const shippingBuckets =
    validDeliveryStrategies?.find(({ type }) => type === selectedStrategyType)?.shippingBuckets ??
    [];

  const strategySelectionTotals = strategySelection?.totals ?? emptyStrategySelectionTotals;
  const strategyAddress = strategySelection?.address;

  const isAllStorePickup =
    strategySelection?.deliverySelections?.every(
      (selection) => selection?.delivery?.type === 'storePickup',
    ) ?? false;

  const isAnyMioHomeDelivery =
    strategySelection?.deliverySelections?.some(
      (selection) => selection?.delivery?.type === 'mioHomeDelivery',
    ) ?? false;

  const deliveryDiscounts =
    deliverySelectionBuckets?.flatMap((bucket) => bucket.appliedDeliveryDiscounts) ?? [];

  const assemblyDiscounts =
    deliverySelectionBuckets?.flatMap((bucket) => bucket.appliedAssemblyDiscounts) ?? [];

  const value = useMemo(
    () => ({
      availableStrategies,
      availableStrategyCount,
      deliverySelectionBuckets,
      shippingBuckets,
      selectedStrategyType,
      selectStrategy,
      selectStrategyError,
      resetSelectStrategyError,
      settingStrategy,
      strategySelectionTotals,
      strategyAddress,
      isAllStorePickup,
      isAnyMioHomeDelivery,
      deliveryDiscounts,
      assemblyDiscounts,
      strategySelection,
      updateAssemblyItems,
      settingAssemblyItems,
      availableAssemblyItems,
      selectedStrategyString,
      strategySelections,
      buildNewStrategySelections,
    }),
    [
      deliverySelectionBuckets,
      selectedStrategyType,
      deliveryStrategies,
      setShippingStrategy,
      settingStrategy,
      deliverySelections,
      selectStrategyError,
      strategySelection,
      updateAssemblyItems,
      settingAssemblyItems,
      availableAssemblyItems,
      strategySelections,
      buildNewStrategySelections,
      isAllStorePickup,
    ],
  );

  return (
    <DeliveryStrategyContext.Provider value={value}>{children}</DeliveryStrategyContext.Provider>
  );
};

export default DeliveryStrategyProvider;
